import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import Cookies from 'js-cookie'
import { COOKIE_KEY } from '../../constants'
import { StrippedVendor } from '../vendors/vendors.types'
import { SingleOrder } from '../orders/orders.type'
import { ApiCartItem, SingleCart } from '../cart/cart.type'
import { SingleTerminal } from '../terminals/terminals.types'
import { CurrentUserResponse, type AuthResponse } from '../user/user.type'
import { FulfilmentCentre } from '../fulfilment-centres/fulfilmentCentres.type'

type AppState = AuthResponse & {
  currentCart?: string
  adminCart?: SingleCart
  adminCartOrder?: SingleOrder
  adminCartItems?: ApiCartItem[]
  POSFulfilmentCenter?: FulfilmentCentre
  POSCreator?: StrippedVendor
  selectedFulfilmentCenters: FulfilmentCentre[]
  selectedPOSTerminal?: SingleTerminal
}

const initialState: AppState = {
  user: null,
  signedIn: false,
  token: undefined,
  currentCart: undefined,
  adminCart: undefined,
  adminCartOrder: undefined,
  adminCartItems: undefined,
  POSFulfilmentCenter: undefined,
  selectedFulfilmentCenters: [],
  selectedPOSTerminal: undefined,
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthUserData: (state, action) => {
      const userData = action.payload.user as CurrentUserResponse

      state.user = userData
      state.token = action.payload.token
      state.signedIn = true
      state.POSFulfilmentCenter = userData.fulfillmentCenter
        ? userData.fulfillmentCenter.find((centre) => centre.role !== 'PARENT')
        : undefined
      state.selectedFulfilmentCenters =
        userData.fulfillmentCenter
          ?.filter((centre) => centre.role !== 'PARENT')
          .slice(0, 1) ?? []
      Cookies.set(COOKIE_KEY, action.payload.token, {
        expires: 1,
      })
    },
    signOut: (state: AppState) => {
      state.user = null
      state.signedIn = false
      state.token = undefined
      state.currentCart = undefined
      state.adminCart = undefined
      state.adminCartOrder = undefined
      state.adminCartItems = undefined
      state.POSFulfilmentCenter = undefined
      state.selectedFulfilmentCenters = []
      state.selectedPOSTerminal = undefined
      Cookies.remove(COOKIE_KEY)
    },
    updateCurrentCartId: (state, action: PayloadAction<string | undefined>) => {
      state.currentCart = action.payload
    },
    updateAdminCart: (
      state,
      action: PayloadAction<
        { adminCart?: SingleCart; adminCartItems?: ApiCartItem[] } | undefined
      >,
    ) => {
      state.adminCart = action.payload?.adminCart
      state.adminCartItems = action.payload?.adminCartItems
    },
    updateAdminCartOrder: (
      state,
      action: PayloadAction<SingleOrder | undefined>,
    ) => {
      state.adminCartOrder = action.payload
    },
    updatePOSFulfilmentCenter: (
      state,
      action: PayloadAction<FulfilmentCentre | undefined>,
    ) => {
      state.POSFulfilmentCenter =
        action.payload?.role === 'PARENT' ? undefined : action.payload
    },
    updatePOSCreator: (
      state,
      action: PayloadAction<StrippedVendor | undefined>,
    ) => {
      state.POSCreator = action.payload
    },
    updateSelectedPOSTerminal: (
      state,
      action: PayloadAction<SingleTerminal | undefined>,
    ) => {
      state.selectedPOSTerminal = action.payload
    },
    updateSelectedFulfilmentCenter: (
      state,
      action: PayloadAction<FulfilmentCentre[]>,
    ) => {
      state.selectedFulfilmentCenters = action.payload
    },
  },
})

export const {
  setAuthUserData,
  signOut,
  updateCurrentCartId,
  updateAdminCart,
  updateAdminCartOrder,
  updatePOSFulfilmentCenter,
  updateSelectedPOSTerminal,
  updateSelectedFulfilmentCenter,
  updatePOSCreator,
} = authSlice.actions
export default authSlice.reducer
